import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import api from './../../utils/api';
import { numberFormat } from './../../utils/functions';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/ContextProvider';
import PaymentTermsModal from './PaymentTermsModal';
import './OmisePayment.css';
import { setPendingPayment, clearPendingPayment } from './../../utils/pendingPayment';
import OnceFooter from './../common/Footer/OnceFooter';

const OMISE_JS_URL = 'https://cdn.omise.co/omise.js';
const POLL_INTERVAL_MS = 3000;

// Readable names for the Omise source types the API may offer.
/**
 * ไอคอนประจำวิธีชำระเงิน
 *
 * ตั้งใจวาดเป็นสัญลักษณ์กลางๆ ไม่ใช้โลโก้จริงของแบรนด์ (PromptPay / ธนาคาร / TrueMoney)
 * เพราะการเอาโลโก้มาใช้มีเงื่อนไขการใช้งานของเจ้าของแบรนด์กำกับอยู่ ถ้าวันหนึ่งได้ไฟล์
 * โลโก้ที่ได้รับอนุญาตมาแล้ว เปลี่ยนเฉพาะในนี้ที่เดียวจบ
 */
const METHOD_ICONS = {
  card: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  ),
  promptpay: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="M14 14h3v3h-3zM20 14h1M14 20h3M20 17v4" />
    </svg>
  ),
  mobile_banking: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="6" y="2" width="12" height="20" rx="2.5" />
      <path d="M11 18h2" />
      <path d="M9 7h6M9.5 10.5v3M12 10.5v3M14.5 10.5v3" />
    </svg>
  ),
  online_direct_debit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-5 9 5" />
      <path d="M5 9v9M10 9v9M14 9v9M19 9v9" />
      <path d="M3 20h18" />
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1" />
      <rect x="3" y="7.5" width="18" height="12" rx="2.5" />
      <circle cx="16.5" cy="13.5" r="1.2" />
    </svg>
  ),
  international_wallet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
    </svg>
  ),
};

const TickIcon = (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12.5l5.5 5.5L20 7" />
  </svg>
);

const LockIcon = (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="10" width="16" height="11" rx="2.5" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const SOURCE_LABELS = {
  promptpay: 'PromptPay',
  mobile_banking_scb: 'SCB',
  mobile_banking_bbl: 'Bangkok Bank',
  mobile_banking_bay: 'Krungsri',
  mobile_banking_kbank: 'KBank',
  mobile_banking_ktb: 'Krungthai',
  internet_banking_bbl: 'Bangkok Bank',
  internet_banking_bay: 'Krungsri',
  truemoney: 'TrueMoney',
  truemoney_jumpapp: 'TrueMoney',
  rabbit_linepay: 'Rabbit LINE Pay',
  shopeepay: 'ShopeePay',
  shopeepay_jumpapp: 'ShopeePay',
  alipayplus_mpm: 'Alipay+',
  wechat_pay_mpm: 'WeChat Pay',
  alipay_cn: 'Alipay (CN)',
  alipay_hk: 'Alipay (HK)',
  kakaopay: 'KakaoPay',
  touch_n_go: "Touch 'n Go",
};

const contextKey = (type, id) => `omise_pay_ctx_${type}_${id}`;
const chargeKey = (type, id) => `omise_pay_charge_${type}_${id}`;

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch (e) {
    return null;
  }
};

export default function OmisePayment() {
  const { type, id } = useParams(); // type: 'booking' | 'cart' | 'main' (closezone)
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'th';
  const { getCurrentHotel } = useContext(AppContext);
  const currentHotel = getCurrentHotel();

  /**
   * โหมดจ่ายยอดคงเหลือ -- มาจากปุ่มในหน้าสถานะการจอง (ลิงก์ SMS / QR ที่หน้างาน)
   *   /once/payment/<type>/<id>?purpose=balance&code=<เลขจอง|เลขตะกร้า>
   *
   * ต่างจากตอนจองตรงที่ห้องจ่ายมัดจำไปแล้ว ไม่มีการกันห้อง/นาฬิกานับถอยหลัง
   * และยอดเงินมาจากเซิร์ฟเวอร์ (omise-payments/balance) ไม่ได้มาจากหน้าก่อนหน้า
   */
  const query = new URLSearchParams(location.search);
  const isBalance = query.get('purpose') === 'balance';
  const balanceCode = query.get('code') || '';
  const [balance, setBalance] = useState(null); // ผลจาก omise-payments/balance
  const storageType = isBalance ? `balance_${type}` : type;

  // Payment context (amount / booking_no) comes from the booking page. Persist it so a
  // page refresh — or coming back from a bank's redirect — can still finish the payment.
  const [ctx] = useState(() => {
    if (isBalance) return {};
    const fromState = location.state;
    if (fromState && fromState.amount) {
      localStorage.setItem(contextKey(type, id), JSON.stringify(fromState));
      return fromState;
    }
    return readJson(contextKey(type, id)) || {};
  });

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [method, setMethod] = useState(null); // the selected method object from config
  const [sourceType, setSourceType] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [card, setCard] = useState({ number: '', name: '', exp_month: '', exp_year: '', cvv: '' });
  const [charge, setCharge] = useState(null); // { id, status, qr_image_uri, ... }
  const [waiting, setWaiting] = useState(false);
  const [paidDone, setPaidDone] = useState(false);
  const pollRef = useRef(null);

  const amount = isBalance ? Number(balance?.amount || 0) : Number(ctx.amount || 0);
  const hotelId = isBalance ? (balance?.hotel_id || currentHotel?.id) : (ctx.hotel_id || currentHotel?.id);
  const refNo = isBalance ? (balance?.ref || balanceCode) : ctx.booking_no;

  // ลิงก์กลับไปหน้ารายการห้องของที่พัก
  //
  // route คือ `/:code` ไม่ใช่ `/once/:id` -- `/once/:id` เป็นหน้าจองห้องเดี่ยวและ
  // มันคาด "room id" ส่งรหัสที่พักเข้าไปจะขึ้นว่า "ลิงค์ไม่ถูกต้อง"
  //
  // ที่พักใน localStorage เป็นสำเนาที่ถ่ายไว้ตอนเปิดหน้าที่พักครั้งแรก
  // ถ้าลูกค้าเปิดลิงก์ชำระเงินตรงๆ ในเบราว์เซอร์ใหม่จะไม่มี -- จึงเอา code จาก
  // config ที่หน้านี้โหลดอยู่แล้วเป็นตัวสำรอง
  const hotelCode = currentHotel?.code || config?.hotel?.code || '';
  const hotelHomeUrl = hotelCode ? `/${hotelCode}` : '';

  /**
   * ออกจากหน้าชำระเงินไปหน้าสถานะ
   *
   * ใช้ replace เสมอ -- จ่ายเงินจบแล้วหน้านี้ไม่ควรมีอยู่ในประวัติอีก ถ้าไม่แทนที่
   * ลูกค้ากดย้อนกลับจะเจอฟอร์มจ่ายเงินของรายการที่จ่ายไปแล้ว ซึ่งชวนให้จ่ายซ้ำ
   *
   * `ref` มาจากเซิร์ฟเวอร์ (holdStatus) ใช้ตอนที่ localStorage ถูกล้างไปแล้ว
   * เช่นกดย้อนกลับหรือรีเฟรชหลังจ่ายจบ -- ไม่มีมันจะสร้างลิงก์หน้าสถานะไม่ได้
   */
  const goToStatus = useCallback((ref = null) => {
    localStorage.removeItem(contextKey(storageType, id));
    localStorage.removeItem(chargeKey(storageType, id));

    if (isBalance) {
      // เซิร์ฟเวอร์ประกอบลิงก์หน้าสถานะให้แล้ว (ปิดทั้งที่พักต้องใช้ booking id ตัวแรก ไม่ใช่ main id)
      let path = '';
      try {
        const u = new URL(balance?.status_url || '');
        path = `${u.pathname}${u.search}`;
      } catch (e) {
        path = type === 'cart' ? `/once/cart-status/${id}/${balanceCode}` : `/once/status/${id}/${balanceCode}`;
      }
      navigate(path, { replace: true });
      return;
    }
    clearPendingPayment();

    const bookingNo = ctx.booking_no || ref?.booking_no || '';
    const bookingId = ctx.booking_id || ref?.booking_id || '';

    if (type === 'cart') {
      navigate(`/once/cart-status/${id}/${ctx.transaction_no || ref?.transaction_no || ''}`, { replace: true });
    } else if (type === 'main') {
      // ปิดทั้งที่พัก: จ่ายทีเดียวด้วย main_id แต่หน้าสถานะอ้างด้วย booking id ตัวแรก
      navigate(`/once/status/${bookingId}/${bookingNo}?close_all=true`, { replace: true });
    } else {
      navigate(`/once/status/${id}/${bookingNo}`, { replace: true });
    }
  }, [type, id, ctx, navigate, storageType, isBalance, balance, balanceCode]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback((chargeId) => {
    stopPolling();
    setWaiting(true);
    const check = () => {
      api.request(`omise-payments/${chargeId}/status`, (res) => {
        if (!res) return;
        if (res.status === 'successful') {
          stopPolling();
          setWaiting(false);
          setPaidDone(true); // ค้างหน้า "กำลังยืนยัน" ไว้ระหว่างเปลี่ยนหน้า ไม่ให้ฟอร์มเลือกวิธีจ่ายโผล่
          toast.success(t('omise_payment_success'));
          goToStatus();
        } else if (res.status === 'failed' || res.status === 'expired') {
          stopPolling();
          setWaiting(false);
          setCharge(null);
          localStorage.removeItem(chargeKey(storageType, id));
          toast.error(t('omise_payment_failed'));
        }
      });
    };
    // ถามทันทีหนึ่งครั้งก่อน -- ลูกค้าที่กลับมาจากแอปธนาคารส่วนใหญ่จ่ายเสร็จแล้ว
    // เดิมรอรอบแรกของ setInterval 3 วินาที ระหว่างนั้นเห็นหน้าเลือกวิธีจ่ายเหมือนต้องจ่ายใหม่
    check();
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);
  }, [t, goToStatus, storageType, id]);

  // load config (public key + methods the account actually has enabled)
  const loadConfig = useCallback(() => {
    setLoading(true);
    api.request('omise-payments/config', (res) => {
      setLoading(false);
      if (!res) return;
      setConfig(res);
      const first = (res.methods || [])[0];
      if (first) {
        setMethod(first);
        setSourceType((first.source_types || [])[0] || '');
      }
    }, { hotel_id: hotelId });
  }, [hotelId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!isBalance || !id) return;
    const idField = type === 'cart' ? 'booking_cart_id' : (type === 'main' ? 'booking_main_id' : 'booking_id');
    api.request('omise-payments/balance', (res, success) => {
      // ตอนผิดพลาด api.request ส่ง axios error มา -- เหตุผลจากเซิร์ฟเวอร์อยู่ใน response.data
      const data = success ? res : res?.response?.data;
      setBalance(success && res ? res : { payable: false, reason: data?.reason || 'not_found', message: data?.message });
    }, { [idField]: id, code: balanceCode }, 'GET', { showError: false, forceCallbackError: true });
  }, [isBalance, type, id, balanceCode]);

  // เก็บทางกลับไว้ เผื่อลูกค้ากดย้อนกลับแล้วเจอตะกร้าว่าง (ดู utils/pendingPayment.js)
  useEffect(() => {
    if (!id || isBalance) return;
    // เก็บ hotel_id ไว้ด้วย -- ปุ่ม "กลับไปชำระเงิน" ส่งค่านี้ต่อ ถ้าลูกค้าเปิดลิงก์
    // ในเบราว์เซอร์ใหม่จะไม่มีที่พักใน context ให้หยิบ
    setPendingPayment({ type, id, amount, booking_no: ctx.booking_no || null, hotel_id: hotelId || null });
  }, [type, id, amount, ctx.booking_no, isBalance]);

  /* ---------- เวลาที่เหลือก่อนห้องถูกปล่อยคืน ----------
   *
   * นับจากวินาทีที่เซิร์ฟเวอร์บอกมา ไม่ใช่จากนาฬิกาเครื่องลูกค้า -- ตั้งเวลาเพี้ยนกันได้
   * และพอนับถึงศูนย์จะถามเซิร์ฟเวอร์ซ้ำอีกครั้งก่อนตัดสินว่าหมดเวลาจริง เพราะ cron
   * ที่ยกเลิกการจองเดินคนละจังหวะกับนาฬิกาในหน้าเว็บ
   */
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [holdExpired, setHoldExpired] = useState(false);

  const checkHold = useCallback(() => {
    if (!id || isBalance) return; // จ่ายยอดคงเหลือไม่มีการกันห้อง
    const params = { [type === 'cart' ? 'booking_cart_id' : (type === 'main' ? 'booking_main_id' : 'booking_id')]: id };
    api.request('omise-payments/hold', (res, success) => {
      if (!success || !res) return;
      // จ่ายจบไปแล้ว -- ไม่ว่าจะมาจากกดย้อนกลับ รีเฟรช หรือเปิดลิงก์เก่า
      // ห้ามโชว์ฟอร์มจ่ายเงินเด็ดขาด เพราะจะกลายเป็นชวนให้จ่ายซ้ำ
      if (res.paid) {
        setHoldExpired(false);
        goToStatus(res.status_ref || null);
        return;
      }
      if (!res.alive) {
        setHoldExpired(true);
        setSecondsLeft(0);
        clearPendingPayment();
        return;
      }
      setHoldExpired(false);
      setSecondsLeft(typeof res.seconds_left === 'number' ? res.seconds_left : null);
    }, params, 'GET', { showError: false });
  }, [type, id, goToStatus, isBalance]);

  useEffect(() => {
    checkHold();
  }, [checkHold]);

  useEffect(() => {
    if (secondsLeft === null) return undefined;
    if (secondsLeft <= 0) {
      // อย่าเพิ่งเชื่อนาฬิกาตัวเอง ถามเซิร์ฟเวอร์ก่อนว่ายกเลิกไปแล้วจริงไหม
      checkHold();
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((v) => (v === null ? null : v - 1)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, checkHold]);

  const clock = (total) => {
    const m = Math.floor(total / 60);
    const sec = total % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  /* ---------- ชั้นกันคนออกจากหน้าไปกลางคัน ----------
   *
   * (ก) ปิดแท็บ / รีเฟรช -> beforeunload
   *     เบราว์เซอร์แสดงข้อความมาตรฐานของตัวเอง เขียนเองไม่ได้ และ "จะไม่แสดงเลย"
   *     ถ้าผู้ใช้ยังไม่เคยคลิกอะไรในหน้านี้ (กติกา user activation ของ Chrome/Safari)
   *     จึงพึ่งอย่างเดียวไม่ได้
   *
   * (ข) กดปุ่มย้อนกลับ -> beforeunload ไม่ทำงานเลยเพราะเป็นการเปลี่ยนหน้าใน SPA
   *     แอปนี้ใช้ <BrowserRouter> ซึ่งใช้ hook บล็อกของ react-router ไม่ได้
   *     จึงดัน history entry เพิ่มไว้หนึ่งอัน ให้การกดย้อนกลับครั้งแรกมาโดนเราก่อน
   *     แล้วถามเองด้วยกล่องยืนยันที่คุมข้อความได้
   */
  useEffect(() => {
    if (holdExpired || isBalance) return undefined;
    const onLeave = (e) => {
      e.preventDefault();
      e.returnValue = t('omise_leave_confirm');
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [holdExpired, isBalance, t]);

  useEffect(() => {
    if (holdExpired || isBalance) return undefined;

    window.history.pushState({ omisePayGuard: true }, '');

    const onPop = () => {
      // eslint-disable-next-line no-alert
      const reallyLeave = window.confirm(t('omise_leave_confirm'));
      if (reallyLeave) {
        window.removeEventListener('popstate', onPop);
        window.history.back();
      } else {
        // อยู่ต่อ -- ต้องดัน entry กลับเข้าไป ไม่งั้นกดย้อนกลับครั้งหน้าจะหลุดทันที
        window.history.pushState({ omisePayGuard: true }, '');
      }
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [holdExpired, isBalance, t]);

  // Omise.js is only needed for card tokenisation (raw card data must never reach our server)
  useEffect(() => {
    if (!config || !config.public_key) return;
    if (window.Omise) {
      window.Omise.setPublicKey(config.public_key);
      return;
    }
    const script = document.createElement('script');
    script.src = OMISE_JS_URL;
    script.async = true;
    script.onload = () => window.Omise && window.Omise.setPublicKey(config.public_key);
    document.body.appendChild(script);
  }, [config]);

  // returning from a redirect (mobile banking / wallet): resume the charge we left behind
  useEffect(() => {
    const pending = readJson(chargeKey(storageType, id));
    if (pending && pending.id) {
      setCharge(pending);
      // ยอดคงเหลือ: รอข้อมูลจาก omise-payments/balance ก่อน -- ลิงก์หน้าสถานะมาจากตรงนั้น
      // (ปิดทั้งที่พักต้องใช้ booking id ตัวแรก) ถ้าจ่ายสำเร็จก่อนข้อมูลมาจะพาไปผิดหน้า
      if (isBalance && !balance) return stopPolling;
      startPolling(pending.id);
    }
    return stopPolling;
  }, [storageType, id, startPolling, isBalance, balance]);

  const selectMethod = (m) => {
    setMethod(m);
    setSourceType((m.source_types || [])[0] || '');
  };

  const postCharge = (extra = {}) => {
    const body = {
      hotel_id: hotelId,
      amount,
      is_deposit: isBalance ? false : !!ctx.is_deposit,
      return_uri: `${window.location.origin}/once/payment/${type}/${id}${isBalance ? `?purpose=balance&code=${encodeURIComponent(balanceCode)}` : ''}`,
      // ยอดคงเหลือ: เซิร์ฟเวอร์คำนวณยอดเองจาก purpose + code ไม่ใช้ amount ที่ส่งไป
      ...(isBalance ? { purpose: 'balance', code: balanceCode } : {}),
      ...extra,
    };
    // ฝั่ง API รับได้ทั้ง 3 แบบ -- เลือกให้ตรงกับสิ่งที่กำลังจ่าย
    const idField = type === 'cart' ? 'booking_cart_id' : (type === 'main' ? 'booking_main_id' : 'booking_id');
    body[idField] = id;

    api.request('omise-payments/charge', (res, success) => {
      if (!success || !res) {
        setSubmitting(false);
        return;
      }
      localStorage.setItem(chargeKey(storageType, id), JSON.stringify(res));
      setCharge(res);

      if (res.status === 'successful') {
        setSubmitting(false);
        setPaidDone(true);
        toast.success(t('omise_payment_success'));
        goToStatus();
      } else if (res.authorize_uri) {
        // hand the customer to the bank / wallet; they come back to return_uri
        window.location.href = res.authorize_uri;
      } else {
        setSubmitting(false);
        startPolling(res.id); // QR flow — wait for the webhook to mark it paid
      }
    }, body, 'POST');
  };

  const handlePay = () => {
    if (!method || amount <= 0) return;
    setSubmitting(true);

    if (method.kind === 'card') {
      if (!window.Omise) {
        setSubmitting(false);
        toast.error(t('omise_payment_failed'));
        return;
      }
      window.Omise.createToken('card', {
        number: card.number.replace(/\s/g, ''),
        name: card.name,
        expiration_month: card.exp_month,
        expiration_year: card.exp_year,
        security_code: card.cvv,
      }, (status, response) => {
        if (status !== 200) {
          setSubmitting(false);
          toast.error(response.message || t('omise_payment_failed'));
          return;
        }
        postCharge({ method: 'card', token: response.id });
      });
      return;
    }

    if (sourceType === 'truemoney' && !phoneNumber) {
      setSubmitting(false);
      toast.warn(t('omise_phone_required'));
      return;
    }
    postCharge({ method: method.key, source_type: sourceType, phone_number: phoneNumber || undefined });
  };

  const methodLabel = (m) => (lang === 'en' ? m.label_en : m.label_th);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isBalance && !balance) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  // จ่ายยอดคงเหลือไม่ได้ (จ่ายครบแล้ว / ที่พักให้จ่ายหน้างาน / ลิงก์ผิด) -- บอกเหตุผลแล้วพากลับหน้าสถานะ
  if (isBalance && !balance.payable && !charge) {
    return (
      <>
        <div className="omise-pay">
          <div className="omise-pay__shell">
            <div className="omise-pay__section text-center">
              <h5 className="mb-2">{balance.reason === 'fully_paid' ? t('balance_fully_paid_title') : t('balance_unavailable_title')}</h5>
              <p className="text-muted mb-3">{balance.message || t('balance_unavailable_hint')}</p>
              {!!refNo && <p className="text-muted">{t('booking_no')}: <strong>{refNo}</strong></p>}
              {balance.reason !== 'not_found' && (
                <button type="button" className="omise-pay__submit" onClick={() => goToStatus()}>
                  {t('balance_back_to_status')}
                </button>
              )}
            </div>
          </div>
        </div>
        <OnceFooter />
      </>
    );
  }

  // หมดเวลาแล้ว -- อย่าให้กดจ่ายได้อีก ไม่งั้นเงินเข้าทั้งที่ห้องถูกปล่อยไปแล้ว
  if (holdExpired) {
    return (
      <>
        <div className="omise-pay">
          <div className="omise-pay__shell">
            <div className="omise-pay__section text-center">
              <h5 className="mb-2">{t('omise_hold_expired_title')}</h5>
              <p className="text-muted mb-3">{t('omise_hold_expired_hint')}</p>
              {!!ctx.booking_no && (
                <p className="text-muted">{t('booking_no')}: <strong>{ctx.booking_no}</strong></p>
              )}
              {!!hotelHomeUrl && (
                <button type="button" className="omise-pay__submit" onClick={() => navigate(hotelHomeUrl)}>
                  {t('book_again')}
                </button>
              )}
            </div>
          </div>
        </div>
        <OnceFooter />
      </>
    );
  }

  if (!config || !(config.methods || []).length) {
    // ทางตัน -- ลูกค้ามีการจองที่ยังไม่จ่ายอยู่ในมือ ต้องบอกให้ชัดว่ายังไม่จบ
    // และให้ทางไปต่อ ไม่ใช่ปล่อยค้างอยู่กับข้อความเดียว
    return (
      <div className="container py-5 text-center">
        <p className="mb-2">{t('omise_no_methods')}</p>
        <p className="text-muted">{t('omise_no_methods_hint')}</p>
        {!!ctx.booking_no && (
          <p className="text-muted">
            {t('booking_no')}: <strong>{ctx.booking_no}</strong>
          </p>
        )}
        <button type="button" className="btn btn-primary mt-2" onClick={loadConfig}>
          {t('try_again')}
        </button>
      </div>
    );
  }

  /**
   * จ่ายสำเร็จแล้ว หรือมีรายการจ่ายที่ค้างอยู่ซึ่งไม่ใช่ QR (กลับมาจากแอปธนาคาร / 3DS ของบัตร)
   * -- แสดงหน้ากำลังยืนยัน ห้ามโชว์ฟอร์มเลือกวิธีจ่าย เพราะลูกค้าจะนึกว่าต้องจ่ายใหม่
   */
  if (paidDone || (charge && !charge.qr_image_uri)) {
    return (
      <>
        <div className="omise-pay">
          <div className="omise-pay__shell">
            <div className="omise-pay__section text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <h5 className="mb-2">{paidDone ? t('omise_payment_success') : t('omise_confirming_payment')}</h5>
              {!paidDone && <p className="text-muted mb-0">{t('omise_confirming_payment_hint')}</p>}
              {!!refNo && <p className="text-muted mt-2 mb-0">{t('booking_no')}: <strong>{refNo}</strong></p>}
            </div>
          </div>
        </div>
        <OnceFooter />
      </>
    );
  }

  // QR shown — customer scans, we poll until the webhook confirms
  if (charge && charge.qr_image_uri) {
    return (
      <>
        <div className="omise-pay">
          <div className="omise-pay__shell">
            <div className="omise-pay__summary">
              <div className="omise-pay__summary-label">{isBalance ? t('balance_amount_due') : t('omise_amount_due')}</div>
              <div className="omise-pay__amount">฿{numberFormat(amount, true)}</div>
              {!!refNo && <div className="omise-pay__ref">{refNo}</div>}
              {secondsLeft !== null && (
                <div className={`omise-pay__countdown${secondsLeft <= 120 ? ' is-soon' : ''}`}>
                  <span className="omise-pay__countdown-label">{t('omise_hold_countdown')}</span>
                  <span className="omise-pay__countdown-clock">{clock(secondsLeft)}</span>
                </div>
              )}
            </div>
            <div className="omise-pay__section text-center">
              <div className="omise-pay__step justify-content-center">
                <span className="omise-pay__step-no">2</span>
                <span className="omise-pay__step-title">{t('omise_scan_qr')}</span>
              </div>
              <img src={charge.qr_image_uri} alt="PromptPay QR" className="omise-pay__qr" />
              <div className="omise-pay__waiting">
                {waiting && <Spinner animation="border" size="sm" />}
                {t('omise_waiting_payment')}
              </div>
            </div>
          </div>
        </div>
        <OnceFooter />
      </>
    );
  }

  const providerChoices = method && method.kind !== 'card' ? (method.source_types || []) : [];
  const needsProviderPick = providerChoices.length > 1;
  const needsPhone = sourceType === 'truemoney';
  const isCard = !!method && method.kind === 'card';
  const hasDetailStep = isCard || needsProviderPick || needsPhone;

  return (
    <>
      <div className="omise-pay">
        <div className="omise-pay__shell">

          <div className="omise-pay__summary">
            <div className="omise-pay__summary-label">{isBalance ? t('balance_amount_due') : t('omise_amount_due')}</div>
            <div className="omise-pay__amount">฿{numberFormat(amount, true)}</div>
            {!!refNo && <div className="omise-pay__ref">{refNo}</div>}
            {secondsLeft !== null && (
              <div className={`omise-pay__countdown${secondsLeft <= 120 ? ' is-soon' : ''}`}>
                <span className="omise-pay__countdown-label">{t('omise_hold_countdown')}</span>
                <span className="omise-pay__countdown-clock">{clock(secondsLeft)}</span>
              </div>
            )}
          </div>

          <div className="omise-pay__section">
            <div className="omise-pay__step">
              <span className="omise-pay__step-no">1</span>
              <span className="omise-pay__step-title">{t('omise_choose_method')}</span>
            </div>
            <div className="omise-pay__methods">
              {config.methods.map((m) => {
                const selected = !!method && method.key === m.key;
                // ชื่อผู้ให้บริการจริงใต้ชื่อหมวด -- "โมบายแบงก์กิ้ง" เฉยๆ ไม่บอกว่าธนาคารไหนบ้าง
                const providers = (m.source_types || [])
                  .map((sc) => SOURCE_LABELS[sc])
                  .filter(Boolean);
                const shown = providers.slice(0, 3).join(' · ');
                const more = providers.length > 3 ? ` +${providers.length - 3}` : '';
                return (
                  <button
                    type="button"
                    key={m.key}
                    className="omise-pay__method"
                    aria-pressed={selected}
                    onClick={() => selectMethod(m)}
                  >
                    <span className="omise-pay__method-tick">{TickIcon}</span>
                    <span className="omise-pay__method-badge">
                      <span className="omise-pay__method-icon">{METHOD_ICONS[m.key] || METHOD_ICONS.card}</span>
                    </span>
                    <span className="omise-pay__method-label">{methodLabel(m)}</span>
                    {!!shown && <span className="omise-pay__method-sub">{shown}{more}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {hasDetailStep && (
            <div className="omise-pay__section omise-pay__fields">
              <div className="omise-pay__step">
                <span className="omise-pay__step-no">2</span>
                <span className="omise-pay__step-title">
                  {isCard ? t('omise_card_number') : (needsProviderPick ? t('omise_select_provider') : t('omise_phone_number'))}
                </span>
              </div>

              {needsProviderPick && (
                <Form.Group className={needsPhone ? 'mb-3' : ''}>
                  <Form.Select value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                    {providerChoices.map((sc) => (
                      <option key={sc} value={sc}>{SOURCE_LABELS[sc] || sc}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {needsPhone && (
                <Form.Group>
                  <Form.Label>{t('omise_phone_number')}</Form.Label>
                  <Form.Control
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="08XXXXXXXX"
                    inputMode="tel"
                  />
                </Form.Group>
              )}

              {isCard && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('omise_card_number')}</Form.Label>
                    <Form.Control
                      value={card.number}
                      onChange={(e) => setCard({ ...card, number: e.target.value })}
                      placeholder="4242 4242 4242 4242"
                      inputMode="numeric"
                      autoComplete="cc-number"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('omise_card_name')}</Form.Label>
                    <Form.Control
                      value={card.name}
                      onChange={(e) => setCard({ ...card, name: e.target.value })}
                      autoComplete="cc-name"
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col xs={4}>
                      <Form.Label>{t('omise_card_exp_month')}</Form.Label>
                      <Form.Control value={card.exp_month} onChange={(e) => setCard({ ...card, exp_month: e.target.value })} placeholder="12" inputMode="numeric" autoComplete="cc-exp-month" />
                    </Col>
                    <Col xs={4}>
                      <Form.Label>{t('omise_card_exp_year')}</Form.Label>
                      <Form.Control value={card.exp_year} onChange={(e) => setCard({ ...card, exp_year: e.target.value })} placeholder="2027" inputMode="numeric" autoComplete="cc-exp-year" />
                    </Col>
                    <Col xs={4}>
                      <Form.Label>{t('omise_card_cvv')}</Form.Label>
                      <Form.Control value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} placeholder="123" inputMode="numeric" autoComplete="cc-csc" />
                    </Col>
                  </Row>
                </>
              )}
            </div>
          )}

          <div className="omise-pay__section">
            {!isBalance && (
              <p className="omise-pay__keep-open">
                {secondsLeft !== null && secondsLeft <= 120 ? t('omise_hold_soon') : t('omise_hold_warning')}
              </p>
            )}
            <button
              type="button"
              className="omise-pay__submit"
              onClick={handlePay}
              disabled={submitting || amount <= 0 || !method}
            >
              {submitting
                ? <Spinner animation="border" size="sm" />
                : `${t('omise_pay_now')} ฿${numberFormat(amount, true)}`}
            </button>
            {/* ยอมรับโดยการกดจ่าย ไม่มีช่องติ๊กเพิ่ม -- ลูกค้าติ๊กยอมรับเงื่อนไขที่พักและ
                นโยบายความเป็นส่วนตัวไปแล้วตอนจอง การบังคับติ๊กซ้ำตรงนี้คือการเพิ่มด่าน
                ในจังหวะที่ห้องถูกกันไว้และนาฬิกากำลังเดิน */}
            <p className="omise-pay__terms">
              {t('terms_pay_agree_prefix')}{' '}
              <button type="button" onClick={() => setShowTerms(true)}>
                {t('terms_pay_link')}
              </button>
            </p>
            <div className="omise-pay__secure">
              {LockIcon}
              <span>Omise</span>
            </div>
          </div>

        </div>
      </div>
      <OnceFooter />
      <PaymentTermsModal show={showTerms} onHide={() => setShowTerms(false)} />
    </>
  );
}
